/* eslint-disable */
import { Button } from '@radix-ui/themes'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { Alert, AlertText } from 'components/Alert'

import CantileverButton from 'components/CantileverButton'

import { ErrorScreen } from 'components/ErrorScreen'

import { AssetViz, AssetVizController } from 'features/assetViz'
import {
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
  PermissionsGate,
} from 'features/auth'
import { BaseMultiViewerPage } from 'pages/viewer/components/BaseMultiViewerPage'
import { ControllerComponent } from 'pages/viewer/configurationController/ConfigurationController'
import {
  MultiComponentViewProvider,
  useMultiComponentView,
} from 'pages/viewer/MultiComponentViewContext'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  BaseAssetModelData,
  GeckoAssetModel,
  GeckoComponentModel,
  SceneConfig,
} from 'types'
import { z } from 'zod'

const AlertContainer = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
`

const HeaderSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 0.5rem;
`

interface HeaderSectionProps {
  children: React.ReactNode
}

export const HeaderSection: React.FC<HeaderSectionProps> = ({ children }) => {
  return <HeaderSectionContainer>{children}</HeaderSectionContainer>
}

const MarkupSectionContainer = styled.div`
  max-height: fit-content;
  padding-right: 7px;
  height: 100%;
`

interface MarkupSectionProps {
  children: React.ReactNode
}

export const MarkupSection: React.FC<MarkupSectionProps> = ({ children }) => {
  return <MarkupSectionContainer>{children}</MarkupSectionContainer>
}

const StyledButton = styled(Button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  height: 2rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.secondaryText};
  background-color: ${({ theme }) => theme.forecast.defaultGhostButton};
  border-radius: 0.5rem;
  font-size: 0.875rem;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.activeGhostButton};
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

interface SidebarButtonProps {
  onClick: () => void
  children: React.ReactNode
}

export const SidebarButton: React.FC<SidebarButtonProps> = ({
  onClick,
  children,
}) => {
  return <StyledButton onClick={onClick}>{children}</StyledButton>
}

export const Route = createFileRoute('/viewer/$unitID/configuration')({
  component: RouteComponent,
  validateSearch: zodValidator(
    z.object({
      siteID: z.string().optional(),
      mode: z.enum(['2D', '3D']).optional(),
      selectedComponent: z.string().optional(),
      selectedComponentId: z.string().optional(),
      position: z
        .object({
          x: z.number().optional(),
          y: z.number().optional(),
          z: z.number().optional(),
        })
        .optional(),
      rotation: z
        .object({
          x: z.number().optional(),
          y: z.number().optional(),
          z: z.number().optional(),
        })
        .optional(),
    })
  ),
})

function RouteComponent() {
  const { unitID } = Route.useParams()

  return (
    <MultiComponentViewProvider unitID={unitID} viewer='configuration'>
      <ConfigurationControllerComponent />
    </MultiComponentViewProvider>
  )
}

function ConfigurationControllerComponent() {
  const [isOpen, setIsOpen] = useState(true)
  const navigate = useNavigate()
  const {
    mode,
    components,
    assetModel,
    markupLayers,
    selectedComponentId,
    setSelectedComponentId,
    componentDataFrames,
    addComponentToScene,
    forceRefreshAssetModelData,
  } = useMultiComponentView()

  const hasLegacyCone = Array.from(assetModel?.data.components ?? []).some(
    ([, component]) => {
      if (component.type === 'legacy_cone') {
        return true
      }
      return false
    }
  )
  const hasComponents = assetModel && assetModel.data.components.size > 0

  // Track if initial setup has been done
  const initialSetupDoneRef = React.useRef(false)

  // Get components that have scene configs defined
  const componentsWithSceneConfig = React.useMemo(() => {
    if (!assetModel) return new Set<string>()

    const result = new Set(
      Array.from(assetModel.data.components.values())
        .filter((component) => {
          const sceneConfig =
            component.sceneConfig ??
            (component.json?.scene_config as SceneConfig)
          // A component is in the scene if it has a scene config defined
          const hasValidConfig = !!sceneConfig
          return hasValidConfig
        })
        .map((component) => component.id)
    )
    return result
  }, [assetModel])

  // Initial setup: if no component is selected and initial setup hasn't been done, select the first one and add it to scene
  useEffect(() => {
    //if the are components that have a sceneConfig defined don't add them to the scene since that removes their originial position
    const hasComponentsInScene = Array.from(
      assetModel?.data.components ?? []
    ).some((d) => {
      const sceneConfig =
        d[1]?.sceneConfig?.config_3d || d[1]?.sceneConfig?.config_2d
      return !!sceneConfig
    })
    if (
      hasComponents &&
      !hasComponentsInScene &&
      !selectedComponentId &&
      !initialSetupDoneRef.current
    ) {
      const firstComponent = Array.from(assetModel.data.components.values())[0]
      if (firstComponent) {
        addComponentToScene(firstComponent.id)
        setSelectedComponentId(firstComponent.id)
        initialSetupDoneRef.current = true
      }
    }
  }, [
    hasComponents,
    selectedComponentId,
    assetModel,
    setSelectedComponentId,
    addComponentToScene,
  ])

  // Component selection should be independent of scene membership
  // Users can select any component, but only those in the scene will be visible in AssetViz

  const memoizedAssetModel = useMemo(() => {
    if (!assetModel) return undefined
    return {
      ...assetModel,
      data: {
        ...assetModel.data,
        components: new Map(
          Array.from(assetModel?.data.components ?? []).filter(
            ([, component]) => componentsWithSceneConfig.has(component.id)
          )
        ),
      },
      asJSON: assetModel.asJSON.bind({
        ...assetModel,
        data: {
          ...assetModel.data,
          components: new Map(
            Array.from(assetModel?.data.components ?? []).filter(
              ([, component]) => componentsWithSceneConfig.has(component.id)
            )
          ),
        },
      }),
    } as GeckoAssetModel<BaseAssetModelData>
  }, [assetModel, componentsWithSceneConfig])

  const assetOpacity = React.useMemo(() => {
    const assetOpacity = (componentModel: GeckoComponentModel | undefined) => {
      const result = !selectedComponentId
        ? 1.0
        : componentModel?.id === selectedComponentId
          ? 1.0
          : 0.5
      return result
    }
    return assetOpacity
  }, [selectedComponentId, componentsWithSceneConfig])

  const assetVizRef = React.useRef<AssetVizController>(null)

  return (
    <PermissionsGate
      requires={[PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR]}
      fallback={
        <ErrorScreen
          title='Permissions Error'
          message='You do not have permissions to access configuration. Please reach out to your admin for help.'
          children={
            <CantileverButton onClick={() => navigate({ to: '/assets' })}>
              Go back to Assets
            </CantileverButton>
          }
        />
      }>
      <BaseMultiViewerPage
        isSidebarOpen={isOpen}
        setIsSidebarOpen={setIsOpen}
        sidebarChildren={
          <MarkupSection>
            <ControllerComponent
              forceRefreshAssetModelData={forceRefreshAssetModelData}
              assetVizRef={assetVizRef}
            />
          </MarkupSection>
        }
        mainPageChildren={
          <>
            <AlertContainer>
              {hasLegacyCone && (
                <Alert color={'red'} text=' '>
                  <AlertText>
                    This asset contains a legacy cone component!
                  </AlertText>
                </Alert>
              )}
            </AlertContainer>
            {!assetModel ? null : (
              <AssetViz
                ref={assetVizRef}
                sceneConfig={{
                  show3D: mode === '3D',
                  assetOpacity,
                  heatmapOpacity: assetOpacity,
                  showFeatures: true,
                  showFeatureLabels: true,
                }}
                assetModel={memoizedAssetModel}
                components={components}
                colorThreshold={undefined}
                markupLayers={markupLayers}
                showMultiComponentView
                showAxesHelper
                componentDataFrames={[...componentDataFrames]}
              />
            )}
          </>
        }
      />
    </PermissionsGate>
  )
}
